import React, { useState, useMemo } from 'react';
import { View, ScrollView, Platform, Pressable } from 'react-native';
import { useRouter, usePathname } from 'expo-router';
import { getDocsPageNav } from '@/constants/docsNavigation';
import * as Clipboard from 'expo-clipboard';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import { useLanding } from '@/components/Landing/LandingKit';
import Icon from '@/components/Icon';
import { GUIDE_DATE_MODIFIED, GUIDE_DATE_PUBLISHED, formatGuideBylineDate } from '@/constants/guideSeoDates';

// ============================================================================
// DOCS HEADER
// ============================================================================
export const DocsHeader = ({ title, description, badge, dateModified, datePublished, showDate = true }) => {
  const { c, isXS } = useLanding();
  const modifiedLabel = formatGuideBylineDate(dateModified || GUIDE_DATE_MODIFIED);
  const publishedLabel = formatGuideBylineDate(datePublished || GUIDE_DATE_PUBLISHED);
  return (
    <View style={{ marginBottom: 36 }}>
      {badge && <Text style={{ color: c.accent, fontSize: 13, lineHeight: 21, marginBottom: 16 }}>{badge}</Text>}
      <Text tag="h1" weight="semibold" style={{ color: c.ink, fontSize: isXS ? 34 : 42, lineHeight: isXS ? 39 : 48, letterSpacing: -1.3 }}>{title}</Text>
      {description && <Text tag="p" style={{ color: c.inkSoft, fontSize: 17, lineHeight: 28, marginTop: 18 }}>{description}</Text>}
      {showDate && modifiedLabel && <Text tag="p" style={{ color: c.muted, fontSize: 12, lineHeight: 20, marginTop: 18 }}>{publishedLabel && publishedLabel !== modifiedLabel ? `Published ${publishedLabel} · Updated ${modifiedLabel}` : `Updated ${modifiedLabel}`}</Text>}
    </View>
  );
};

// ============================================================================
// DOCS SECTION — auto-generates an anchor ID from the title
// ============================================================================
const slugify = (str) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export const DocsSection = ({ title, children, id }) => {
  const { isXS } = useResponsiveness();
  const sectionId = id || (title ? slugify(title) : undefined);

  return (
    <View style={{ marginBottom: isXS ? 40 : 56 }} nativeID={sectionId}>
      {title && (
        <Text
          tag="h2"
          weight="semibold"
          style={{
            fontSize: isXS ? 24 : 28,
            letterSpacing: -0.5,
            marginBottom: isXS ? 16 : 20,
          }}
          {...(Platform.OS === 'web' ? { 'data-section-title': 'true' } : {})}
        >
          {title}
        </Text>
      )}
      {children}
    </View>
  );
};

// ============================================================================
// DOCS PARAGRAPH
// ============================================================================
export const DocsParagraph = ({ children, style }) => {
  const { isXS } = useResponsiveness();
  const isDocs = /^\/(docs|learn)(\/|$)/.test(usePathname());

  return (
    <Text
      tag="p"
      size="l"
      color="secondary"
      style={[{ lineHeight: isXS ? 28 : 30, marginBottom: 16 }, isDocs ? { fontSize: 16, lineHeight: 27 } : null, style]}
    >
      {children}
    </Text>
  );
};

// ============================================================================
// DOCS DIVIDER
// ============================================================================
export const DocsDivider = ({ spacing = 32 }) => {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);

  return (
    <View
      style={{
        height: 1,
        backgroundColor: isDark
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(0,0,0,0.08)',
        marginVertical: spacing,
      }}
    />
  );
};

// ============================================================================
// SYNTAX TOKENIZER — lightweight highlighting for code blocks
// ============================================================================
const TOKEN_COLORS = {
  keyword: '#C792EA',
  string: '#C3E88D',
  comment: '#546E7A',
  number: '#F78C6C',
  boolean: '#FF5370',
  variable: '#82AAFF',
  function: '#82AAFF',
  operator: '#89DDFF',
  text: '#E4E4E7',
};

const LANG_PATTERNS = {
  javascript: [
    { type: 'comment', re: /^\/\/.*/ },
    { type: 'comment', re: /^\/\*[\s\S]*?\*\// },
    { type: 'string', re: /^`(?:[^`\\]|\\.)*`/ },
    { type: 'string', re: /^"(?:[^"\\]|\\.)*"/ },
    { type: 'string', re: /^'(?:[^'\\]|\\.)*'/ },
    {
      type: 'keyword',
      re: /^(?:const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|class|import|export|from|default|async|await|try|catch|finally|throw|new|this|typeof|instanceof|of|in|yield)\b/,
    },
    { type: 'boolean', re: /^(?:true|false|null|undefined)\b/ },
    { type: 'number', re: /^(?:0x[\da-fA-F]+|\d+\.?\d*(?:e[+-]?\d+)?)\b/ },
  ],
  python: [
    { type: 'comment', re: /^#.*/ },
    { type: 'string', re: /^"""[\s\S]*?"""/ },
    { type: 'string', re: /^'''[\s\S]*?'''/ },
    { type: 'string', re: /^"(?:[^"\\]|\\.)*"/ },
    { type: 'string', re: /^'(?:[^'\\]|\\.)*'/ },
    {
      type: 'keyword',
      re: /^(?:def|class|if|elif|else|for|while|return|import|from|as|with|try|except|raise|pass|lambda|yield|async|await|in|not|and|or|is|None|True|False)\b/,
    },
    { type: 'number', re: /^(?:0x[\da-fA-F]+|\d+\.?\d*(?:e[+-]?\d+)?)\b/ },
  ],
  bash: [
    { type: 'comment', re: /^#.*/ },
    { type: 'string', re: /^"(?:[^"\\]|\\.)*"/ },
    { type: 'string', re: /^'[^']*'/ },
    { type: 'variable', re: /^\$(?:\w+|\{[^}]+\})/ },
    {
      type: 'keyword',
      re: /^(?:if|then|else|elif|fi|for|while|do|done|case|esac|function|return|exit|export|source|alias|cd|echo|printf|read|set|unset|local|curl|npm|pip|git|sudo|mkdir|chmod|chown)\b/,
    },
  ],
  json: [
    { type: 'string', re: /^"(?:[^"\\]|\\.)*"/ },
    { type: 'number', re: /^-?\d+\.?\d*(?:e[+-]?\d+)?/ },
    { type: 'boolean', re: /^(?:true|false|null)\b/ },
  ],
};

LANG_PATTERNS.typescript = LANG_PATTERNS.javascript;
LANG_PATTERNS.jsx = LANG_PATTERNS.javascript;
LANG_PATTERNS.tsx = LANG_PATTERNS.javascript;
LANG_PATTERNS.sh = LANG_PATTERNS.bash;
LANG_PATTERNS.shell = LANG_PATTERNS.bash;
LANG_PATTERNS.zsh = LANG_PATTERNS.bash;

const tokenize = (code, language) => {
  const patterns = LANG_PATTERNS[language];
  if (!patterns) return [{ type: 'text', value: code }];

  const tokens = [];
  let remaining = code;

  while (remaining.length > 0) {
    let matched = false;
    for (const pattern of patterns) {
      const match = remaining.match(pattern.re);
      if (match) {
        tokens.push({ type: pattern.type, value: match[0] });
        remaining = remaining.slice(match[0].length);
        matched = true;
        break;
      }
    }
    if (!matched) {
      let nextIdx = remaining.length;
      for (const pattern of patterns) {
        const search = remaining.slice(1).search(pattern.re);
        if (search !== -1 && search + 1 < nextIdx) {
          nextIdx = search + 1;
        }
      }
      tokens.push({ type: 'text', value: remaining.slice(0, nextIdx) });
      remaining = remaining.slice(nextIdx);
    }
  }
  return tokens;
};

// ============================================================================
// CODE BLOCK — with syntax highlighting, line numbers, and copy
// ============================================================================
export const CodeBlock = ({
  code,
  language = 'javascript',
  title,
  showLineNumbers = false,
  children,
}) => {
  code = code ?? (typeof children === 'string' ? children.trim() : '') ;
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (Platform.OS === 'web') {
        await navigator.clipboard.writeText(code);
      } else {
        await Clipboard.setStringAsync(code);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      /* ignore */
    }
  };

  const langDotColor =
    language === 'python'
      ? '#3572A5'
      : language === 'bash' || language === 'sh'
        ? '#89e051'
        : language === 'json'
          ? '#F7DF1E'
          : '#61DAFB';

  const lines = code.split('\n');
  const shouldShowLines =
    showLineNumbers || (language !== 'text' && lines.length > 3);

  const highlightedTokens = useMemo(
    () => tokenize(code, language),
    [code, language],
  );

  const renderHighlighted = () => {
    if (language === 'text') {
      return (
        <Text
          tag="pre"
          style={{
            fontFamily:
              Platform.OS === 'web'
                ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                : 'monospace',
            fontSize: isXS ? 14 : 15,
            lineHeight: isXS ? 24 : 26,
            color: TOKEN_COLORS.text,
            whiteSpace: 'pre-wrap',
          }}
        >
          {code}
        </Text>
      );
    }

    return (
      <View style={{ flexDirection: 'row' }}>
        {shouldShowLines && (
          <View
            style={{
              paddingRight: 12,
              marginRight: 12,
              borderRightWidth: 1,
              borderRightColor: 'rgba(255,255,255,0.08)',
              alignItems: 'flex-end',
              userSelect: 'none',
            }}
          >
            {lines.map((_, i) => (
              <Text
                tag="span"
                key={i}
                style={{
                  fontFamily:
                    Platform.OS === 'web'
                      ? 'ui-monospace, SFMono-Regular, Menlo, monospace'
                      : 'monospace',
                  fontSize: isXS ? 14 : 15,
                  lineHeight: isXS ? 24 : 26,
                  color: 'rgba(255,255,255,0.2)',
                }}
              >
                {i + 1}
              </Text>
            ))}
          </View>
        )}
        <Text
          tag="pre"
          style={{
            fontFamily:
              Platform.OS === 'web'
                ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                : 'monospace',
            fontSize: isXS ? 14 : 15,
            lineHeight: isXS ? 24 : 26,
            color: TOKEN_COLORS.text,
            whiteSpace: 'pre',
            flex: 1,
          }}
        >
          {highlightedTokens.map((token, i) => (
            <Text
              tag="span"
              key={i}
              style={{ color: TOKEN_COLORS[token.type] || TOKEN_COLORS.text }}
            >
              {token.value}
            </Text>
          ))}
        </Text>
      </View>
    );
  };

  return (
    <View
      style={{
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 20,
        borderWidth: 1,
        borderColor: isDark
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(0,0,0,0.08)',
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: 16,
          paddingVertical: 10,
          backgroundColor: isDark
            ? 'rgba(255,255,255,0.03)'
            : 'rgba(0,0,0,0.02)',
          borderBottomWidth: 1,
          borderBottomColor: isDark
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(0,0,0,0.06)',
        }}
      >
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
        >
          <View
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: langDotColor,
            }}
          />
          <Text tag="span" size="m" color="secondary" weight="medium">
            {title || language}
          </Text>
        </View>
        <Pressable
          onPress={handleCopy}
          accessibilityRole="button"
          accessibilityLabel="Copy example"
          style={({ hovered }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            paddingHorizontal: 10,
            paddingVertical: 5,
            borderRadius: 6,
            backgroundColor: hovered
              ? isDark
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.05)'
              : 'transparent',
          })}
        >
          <Icon
            name={copied ? 'check' : 'copy'}
            size={12}
            color={copied ? '#34D399' : colors.text_color_secondary}
          />
          <Text tag="span" size="m" color={copied ? 'primary' : 'secondary'}>
            {copied ? 'Copied!' : 'Copy'}
          </Text>
        </Pressable>
      </View>

      <ScrollView
        horizontal={language !== 'text'}
        showsHorizontalScrollIndicator={false}
        style={{
          backgroundColor: isDark ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.9)',
        }}
        contentContainerStyle={{ padding: isXS ? 16 : 20 }}
      >
        {renderHighlighted()}
      </ScrollView>
    </View>
  );
};

// ============================================================================
// TIP BLOCK
// ============================================================================
export const TipBlock = ({
  type = 'info',
  title,
  children,
}) => {
  const { theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);

  const typeConfig = {
    info: { icon: 'circle-info', color: '#3B82F6', label: 'Info' },
    warning: { icon: 'triangle-exclamation', color: '#F59E0B', label: 'Warning' },
    tip: { icon: 'lightbulb', color: '#10B981', label: 'Tip' },
    note: { icon: 'note-sticky', color: '#8B5CF6', label: 'Note' },
  };

  const config = typeConfig[type] || typeConfig.info;

  return (
    <View
      style={{
        flexDirection: 'row',
        gap: 14,
        padding: isXS ? 16 : 20,
        borderRadius: 14,
        backgroundColor: `${config.color}${isDark ? '10' : '08'}`,
        borderWidth: 1,
        borderColor: `${config.color}${isDark ? '25' : '20'}`,
        marginBottom: 20,
      }}
    >
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          backgroundColor: `${config.color}20`,
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={config.icon} size={16} color={config.color} solid />
      </View>
      <View style={{ flex: 1 }}>
        <Text
          tag="h3"
          size="l"
          weight="semibold"
          style={{ color: config.color, marginBottom: 4 }}
        >
          {title || config.label}
        </Text>
        <Text tag="p" size="m" color="secondary" style={{ lineHeight: 24 }}>
          {children}
        </Text>
      </View>
    </View>
  );
};

// ============================================================================
// FEATURE CARD
// ============================================================================
export const FeatureCard = ({
  icon,
  title,
  description,
  color = '#3B82F6',
  href,
}) => {
  const { theme } = useTheme();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const isDark = isDarkTheme(theme);

  const content = (
    <View
      style={{
        width: '100%',
        borderRadius: 20,
        backgroundColor: isDark
          ? 'rgba(255,255,255,0.03)'
          : 'rgba(0,0,0,0.02)',
        borderWidth: 1,
        borderColor: isDark
          ? 'rgba(255,255,255,0.06)'
          : 'rgba(0,0,0,0.06)',
        padding: isXS ? 20 : 24,
        position: 'relative',
      }}
    >
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 24,
          right: 24,
          height: 2,
          backgroundColor: color,
          opacity: 0.5,
          borderRadius: 1,
        }}
      />
      <View
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          backgroundColor: `${color}15`,
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 16,
        }}
      >
        <Icon name={icon} size={20} color={color} solid />
      </View>
      <Text tag="h3" size="xl" weight="semibold" style={{ marginBottom: 8 }}>
        {title}
      </Text>
      <Text tag="p" size="m" color="secondary" style={{ lineHeight: 24 }}>
        {description}
      </Text>
      {href && (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            marginTop: 12,
          }}
        >
          <Text tag="span" size="m" weight="medium" style={{ color }}>
            Learn more
          </Text>
          <Icon name="arrow-right" size={12} color={color} />
        </View>
      )}
    </View>
  );

  if (href) {
    return (
      <Pressable onPress={() => router.push(href)}>{content}</Pressable>
    );
  }
  return content;
};

// ============================================================================
// QUICK LINK CARD
// ============================================================================
export const QuickLinkCard = ({
  icon,
  title,
  description,
  href,
  color = '#3B82F6',
}) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const isDark = isDarkTheme(theme);

  return (
    <Pressable
      onPress={() => router.push(href)}
      style={({ hovered }) => ({
        flex: isXS ? undefined : 1,
        width: isXS ? '100%' : undefined,
        minWidth: isXS ? undefined : 200,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        padding: isXS ? 16 : 18,
        borderRadius: 14,
        backgroundColor: hovered
          ? isDark
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(0,0,0,0.03)'
          : isDark
            ? 'rgba(255,255,255,0.03)'
            : 'rgba(0,0,0,0.015)',
        borderWidth: 1,
        borderColor: hovered
          ? isDark
            ? 'rgba(255,255,255,0.1)'
            : 'rgba(0,0,0,0.08)'
          : isDark
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(0,0,0,0.04)',
        transition: 'all 0.2s ease',
      })}
    >
      <View
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          backgroundColor: `${color}15`,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={icon} size={18} color={color} solid />
      </View>
      <View style={{ flex: 1 }}>
        <Text tag="span" size="l" weight="medium" style={{ marginBottom: 2 }}>
          {title}
        </Text>
        {description && (
          <Text tag="span" size="n" color="secondary" numberOfLines={1}>
            {description}
          </Text>
        )}
      </View>
      <Icon
        name="chevron-right"
        size={14}
        color={colors.text_color_tertiary}
      />
    </Pressable>
  );
};

// ============================================================================
// CHECKLIST ITEM
// ============================================================================
export const ChecklistItem = ({ text }) => {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        marginBottom: 12,
      }}
    >
      <View
        style={{
          width: 22,
          height: 22,
          borderRadius: 11,
          backgroundColor: isDark
            ? 'rgba(52, 211, 153, 0.15)'
            : 'rgba(52, 211, 153, 0.12)',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 2,
        }}
      >
        <Icon name="check" size={11} color="#34D399" solid />
      </View>
      <Text tag="p" size="l" style={{ flex: 1, lineHeight: 26 }}>
        {text}
      </Text>
    </View>
  );
};

// ============================================================================
// DOCS NAVIGATION — Previous/Next
// ============================================================================
export const DocsNavigation = ({ previous, next }) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const isDark = isDarkTheme(theme);

  const NavButton = ({ direction, label, href }) => (
    <Pressable
      onPress={() => router.push(href)}
      accessibilityRole="link"
      accessibilityLabel={`${direction === 'previous' ? 'Previous' : 'Next'}: ${label}`}
      style={({ hovered }) => ({
        flex: 1,
        flexDirection: direction === 'previous' ? 'row' : 'row-reverse',
        alignItems: 'center',
        gap: 12,
        padding: isXS ? 16 : 20,
        borderRadius: 14,
        backgroundColor: hovered
          ? isDark
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(0,0,0,0.03)'
          : isDark
            ? 'rgba(255,255,255,0.03)'
            : 'rgba(0,0,0,0.015)',
        borderWidth: 1,
        borderColor: isDark
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(0,0,0,0.06)',
      })}
    >
      <Icon
        name={direction === 'previous' ? 'arrow-left' : 'arrow-right'}
        size={16}
        color={colors.text_color_secondary}
      />
      <View
        style={{
          flex: 1,
          alignItems: direction === 'previous' ? 'flex-start' : 'flex-end',
        }}
      >
        <Text tag="span" size="n" color="secondary" style={{ marginBottom: 2 }}>
          {direction === 'previous' ? 'Previous' : 'Next'}
        </Text>
        <Text tag="span" size="l" weight="medium">
          {label}
        </Text>
      </View>
    </Pressable>
  );

  return (
    <View
      style={{
        flexDirection: isXS ? 'column' : 'row',
        gap: 16,
        marginTop: 48,
        paddingTop: 32,
        borderTopWidth: 1,
        borderTopColor: isDark
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(0,0,0,0.08)',
      }}
    >
      {previous ? (
        <NavButton
          direction="previous"
          label={previous.label}
          href={previous.href}
        />
      ) : (
        <View style={{ flex: 1 }} />
      )}
      {next ? (
        <NavButton
          direction="next"
          label={next.label}
          href={next.href}
        />
      ) : (
        <View style={{ flex: 1 }} />
      )}
    </View>
  );
};

export const DocsAutoNavigation = () => {
  const pathname = usePathname();
  const { previous, next } = useMemo(() => getDocsPageNav(pathname), [pathname]);
  return <DocsNavigation previous={previous} next={next} />;
};

// ============================================================================
// MODEL BADGE
// ============================================================================
export const ModelBadge = ({ name, provider, color = '#3B82F6' }) => {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 100,
        backgroundColor: isDark
          ? 'rgba(255,255,255,0.04)'
          : 'rgba(0,0,0,0.02)',
        borderWidth: 1,
        borderColor: isDark
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(0,0,0,0.06)',
      }}
    >
      <View
        style={{
          width: 8,
          height: 8,
          borderRadius: 4,
          backgroundColor: color,
          boxShadow: `0 0 8px ${color}60`,
        }}
      />
      <View>
        <Text tag="span" size="m" weight="medium">
          {name}
        </Text>
        {provider && (
          <Text tag="span" size="n" color="secondary">
            {provider}
          </Text>
        )}
      </View>
    </View>
  );
};

// ============================================================================
// STEP LIST
// ============================================================================
export const StepList = ({ children }) => (
  <View style={{ marginBottom: 24 }}>
    {React.Children.map(children, (child, index) => (
      <View key={index}>
        {React.cloneElement(child, { stepNumber: index + 1 })}
      </View>
    ))}
  </View>
);

export const Step = ({ stepNumber, title, children, color = '#3B82F6' }) => {
  const { theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);

  return (
    <View
      style={{
        flexDirection: 'row',
        gap: 16,
        marginBottom: 24,
        paddingBottom: 24,
        borderBottomWidth: 1,
        borderBottomColor: isDark
          ? 'rgba(255,255,255,0.06)'
          : 'rgba(0,0,0,0.04)',
      }}
    >
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: 16,
          backgroundColor: color,
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Text tag="span" size="m" weight="bold" style={{ color: '#fff' }}>
          {stepNumber}
        </Text>
      </View>
      <View style={{ flex: 1 }}>
        <Text tag="h3" size="xl" weight="semibold" style={{ marginBottom: 8 }}>
          {title}
        </Text>
        <Text tag="p" size="l" color="secondary" style={{ lineHeight: 28 }}>
          {children}
        </Text>
      </View>
    </View>
  );
};

// ============================================================================
// GRID
// ============================================================================
export const DocsGrid = ({ children, columns = 2 }) => {
  const { isXS, isSM } = useResponsiveness();
  const gap = 12;
  const effectiveColumns = isXS ? 1 : isSM ? Math.min(columns, 2) : columns;
  const gapDeduction =
    effectiveColumns > 1
      ? (gap * (effectiveColumns - 1)) / effectiveColumns
      : 0;
  const columnPercent = 100 / effectiveColumns;

  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap,
        marginBottom: 24,
      }}
    >
      {React.Children.map(children, (child) => (
        <View
          style={{
            width:
              effectiveColumns === 1
                ? '100%'
                : `calc(${columnPercent}% - ${gapDeduction}px)`,
            flexGrow: 0,
            flexShrink: 0,
          }}
        >
          {child}
        </View>
      ))}
    </View>
  );
};
